<template>
  <BaseModal
    :heading="$t('marketing.email.automated_emails.rebook_setup.title')"
    :loading="$apollo.loading"
    small
    :parentRoute="{
      name: parentRouteName
    }"
  >
    <Flex direction="column">
      <BaseText>
        {{ $t('marketing.email.automated_emails.rebook_setup.info') }}
      </BaseText>
      <CategoryRow
        v-for="(category, index) in categories"
        :key="index"
        :category="category"
      />
      <BaseAlert
        v-if="!numberOfServicesWithRebookEnabled"
        color="warning"
        :text="$t('marketing.email.automated_emails.rebook_setup.alert')"
        :primaryAction="
          $t('marketing.email.automated_emails.rebook_setup.alert_action')
        "
        @primaryAction="disableRebook"
        v-test="'rebook-setup-alert'"
      />
    </Flex>
    <template #footerSub>
      <BaseText
        v-if="hasServicesWithoutRebook"
        iconBefore="refresh"
        @click="resetToDefault"
      >
        {{ $t('marketing.email.automated_emails.rebook_setup.reset') }}
      </BaseText>
    </template>
    <template #footer>
      <BaseButton
        color="inverted"
        @click="$router.push({ name: parentRouteName })"
      >
        {{ $t('global.actions.cancel') }}
      </BaseButton>
      <BaseButton
        :loading="isLoading"
        :disabled="!numberOfServicesWithRebookEnabled"
        @click="onSave"
        v-test="'rebook-setup-save'"
      >
        {{ $t('global.actions.save') }}
      </BaseButton>
    </template>
  </BaseModal>
</template>

<script
  setup
  lang="ts"
>
import { useMutation } from '@vue/apollo-composable';
import { storeToRefs } from 'pinia';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useLeaveConfirmation } from '@/helpers/routing';
import { flash } from '@/helpers/ui';
import { useCompanyStore } from '@/stores/company';
import { useServicesStore } from '@/stores/services';
import { useMarketingBuilderStore } from '../../store/builder';
import CategoryRow from './CategoryRow.vue';
import { UPDATE_SERVICES } from './graphql';

const builderStore = useMarketingBuilderStore();
const { categoriesWithServices } = storeToRefs(useServicesStore());
const { updateCompany } = useCompanyStore();

const { t } = useI18n();
const router = useRouter();

const hasServicesWithoutRebook = computed(
  () =>
    categories.value
      .map((category: { services: any }) => category.services)
      .reduce((acc: string | any[], val: any) => acc.concat(val), [])
      .filter((service: { rebookEnabled: any }) => !service.rebookEnabled)
      .length,
);
const numberOfServicesWithRebookEnabled = ref(
  categoriesWithServices.value
    .map((category: { services: any }) => category.services)
    .reduce((acc: string | any[], val: any) => acc.concat(val), [])
    .filter((service: { rebookEnabled: any }) => service.rebookEnabled).length,
);

const categories = ref(
  categoriesWithServices.value
    .map(
      (category: { id: any; name: any; color: string; services: any[] }) => ({
        id: category.id,
        name: category.name,
        color: category.color,
        services: category.services.map(
          (service: {
            id: any;
            name: any;
            rebookEnabled: any;
            rebookPeriod: any;
          }) => ({
            id: service.id,
            name: service.name,
            rebookEnabled:
              !numberOfServicesWithRebookEnabled.value ||
              service.rebookEnabled ||
              false,
            rebookPeriod: service.rebookPeriod || 30,
          }),
        ),
      }),
    )
    .filter(
      (category: { services: string | any[] }) => !!category.services.length,
    ),
);

const { resetConfirmation, confirmLeave } = useLeaveConfirmation(
  reactive({
    categories,
  }),
);

watch(categories, () => (confirmLeave.value = true), { deep: true });

watch(
  categories,
  () => {
    numberOfServicesWithRebookEnabled.value = categories.value
      .map((category: { services: any }) => category.services)
      .reduce((acc: string | any[], val: any) => acc.concat(val), [])
      .filter(
        (service: { rebookEnabled: any }) => service.rebookEnabled,
      ).length;
  },
  {
    immediate: true,
    deep: true,
  },
);

const parentRouteName = 'rebook-reminder-dashboard';
const isLoading = ref(false);

const resetToDefault = () => {
  categories.value.forEach(
    (category: { services: { rebookEnabled: boolean }[] }) => {
      category.services.forEach((service: { rebookEnabled: boolean }) => {
        service.rebookEnabled = true;
      });
    },
  );
};

const disableRebook = () => {
  confirmLeave.value = false;
  const input = {
    settings: {
      bookings: {
        rebookReminderEnabled: false,
      },
    },
  };

  updateCompany(input).then(() => {
    builderStore.setEnabled(false);
    router.push({ name: parentRouteName });
    flash(t('global.flash.email_disabled'));
  });
};

const { mutate } = useMutation(UPDATE_SERVICES);

const onSave = () => {
  resetConfirmation();
  const bulkUpdateServiceAttributes = categories.value
    .map((category: { services: any }) =>
      category.services.map(
        (service: {
          id: number;
          rebookEnabled: boolean;
          rebookPeriod: number;
        }) => ({
          id: service.id,
          rebookEnabled: service.rebookEnabled,
          rebookPeriod: service.rebookPeriod,
        }),
      ),
    )
    .reduce((acc: string | any[], val: any) => acc.concat(val), []);

  isLoading.value = true;
  mutate({
    input: {
      bulkUpdateServiceAttributes,
    },
  })
    .then(() => {
      const { getServices } = useServicesStore();
      getServices();

      flash(t('global.flash.services_updated'));
      router.push({ name: parentRouteName });
    })
    .finally(() => {
      isLoading.value = false;
    });
};
</script>
